import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users,
  Plus,
  Trash2,
  Edit,
  Clock,
  RefreshCw,
  Shield,
  User,
  X,
  Key,
  Timer,
  CheckCircle,
  XCircle,
  ArrowLeft,
  Power,
  PowerOff,
  Phone,
  Settings,
  MessageSquare,
  Mail,
  Globe,
  Eye,
  UserPlus,
  Ban,
  TrendingUp,
  GraduationCap,
  History,
} from "lucide-react";
import axios from "axios";
import { LoginHistoryModal } from "./LoginHistoryModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminPanel({ token, onBack, currentUser, onUserUpdate }) {
  const [users, setUsers] = useState([]);
  const [contactRequests, setContactRequests] = useState([]);
  const [historyUser, setHistoryUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("users");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    role: "user",
    expires_in_hours: "",
    can_view_signals: true,
    can_access_training: false,
    signal_tier: "none",
    tier_education: true,
    tier_basic: false,
    tier_advanced: false,
    is_active: true,
    activate_days: "",
  });
  const [profileData, setProfileData] = useState({
    name: currentUser?.name || "",
    phone: currentUser?.phone || "",
    current_password: "",
    new_password: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`, { headers });
      setUsers(res.data.users || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchContactRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/contact-requests`, { headers });
      setContactRequests(res.data.requests || []);
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
    fetchContactRequests();
  }, [fetchUsers, fetchContactRequests]);

  useEffect(() => {
    if (currentUser) {
      setProfileData({
        name: currentUser.name || "",
        phone: currentUser.phone || "",
        current_password: "",
        new_password: "",
      });
    }
  }, [currentUser]);

  const createUser = async () => {
    setError("");
    try {
      const payload = {
        ...newUser,
        phone: newUser.phone || null,
        expires_in_hours: newUser.expires_in_hours ? parseInt(newUser.expires_in_hours) : null,
        can_view_signals: newUser.can_view_signals,
        can_access_training: newUser.can_access_training,
        tier_education: newUser.tier_education,
        tier_basic: newUser.tier_basic,
        tier_advanced: newUser.tier_advanced,
        // derived (advanced > basic > none) — backend re-derives anyway
        signal_tier: newUser.tier_advanced ? "advanced" : (newUser.tier_basic ? "basic" : "none"),
        activate_days: newUser.activate_days ? parseInt(newUser.activate_days) : null,
      };
      await axios.post(`${API}/admin/users`, payload, { headers });
      // If user should start as inactive, immediately deactivate
      if (!newUser.is_active) {
        const created = await axios.get(`${API}/admin/users`, { headers });
        const justCreated = (created.data.users || []).find((u) => u.email === newUser.email);
        if (justCreated) {
          await axios.put(`${API}/admin/users/${justCreated.id}`, { is_active: false }, { headers });
        }
      }
      setSuccess("User created successfully");
      setShowAddModal(false);
      setNewUser({ name: "", email: "", password: "", phone: "", role: "user", expires_in_hours: "", can_view_signals: true, can_access_training: false, signal_tier: "none", tier_education: true, tier_basic: false, tier_advanced: false, is_active: true, activate_days: "" });
      fetchUsers();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create user");
    }
  };

  const updateProfile = async () => {
    setError("");
    try {
      const payload = {
        name: profileData.name,
        phone: profileData.phone || null,
      };
      if (profileData.new_password) {
        payload.new_password = profileData.new_password;
        payload.current_password = profileData.current_password;
      }
      const res = await axios.put(`${API}/auth/profile`, payload, { headers });
      setSuccess("Profile updated successfully");
      setShowProfileModal(false);
      if (onUserUpdate) {
        onUserUpdate(res.data.user);
      }
      setProfileData({ ...profileData, current_password: "", new_password: "" });
      fetchUsers();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update profile");
    }
  };

  const updateUser = async () => {
    setError("");
    try {
      const ta = editUser.tier_advanced ?? (editUser.signal_tier === "advanced");
      const tb = editUser.tier_basic ?? (editUser.signal_tier === "basic");
      const te = editUser.tier_education ?? (!ta && !tb);
      const payload = {
        name: editUser.name,
        email: editUser.email,
        phone: editUser.phone || null,
        role: editUser.role,
        is_active: editUser.is_active,
        can_view_signals: editUser.can_view_signals,
        can_access_training: editUser.can_access_training,
        tier_education: te,
        tier_basic: tb,
        tier_advanced: ta,
        signal_tier: ta ? "advanced" : (tb ? "basic" : "none"),
      };
      await axios.put(`${API}/admin/users/${editUser.id}`, payload, { headers });
      setSuccess("User updated successfully");
      setEditUser(null);
      fetchUsers();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update user");
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { is_active: !currentStatus }, { headers });
      setSuccess(currentStatus ? "User deactivated" : "User activated");
      fetchUsers();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update user status");
      setTimeout(() => setError(""), 3000);
    }
  };

  const renewMembership = async (userId, userName) => {
    const input = window.prompt(`Activate "${userName}" for how many DAYS?\n(Default: 30)`, "30");
    if (input === null) return;
    const days = parseInt(input, 10);
    if (isNaN(days) || days < 1) {
      setError("Invalid number of days");
      setTimeout(() => setError(""), 3000);
      return;
    }
    try {
      await axios.post(`${API}/admin/users/${userId}/renew-membership?days=${days}`, {}, { headers });
      setSuccess(`Membership activated for ${days} day(s)`);
      fetchUsers();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to renew membership");
      setTimeout(() => setError(""), 3000);
    }
  };

  const resetDevice = async (userId) => {
    if (!window.confirm("Reset device binding? The user will be able to login from a new device.")) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/reset-device`, {}, { headers });
      setSuccess("Device binding reset");
      fetchUsers();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset device");
      setTimeout(() => setError(""), 3000);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers });
      setSuccess("User deleted successfully");
      fetchUsers();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete user");
      setTimeout(() => setError(""), 3000);
    }
  };

  const extendAccess = async (userId, hours) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/extend?hours=${hours}`, {}, { headers });
      setSuccess(`Access extended by ${hours} hours`);
      fetchUsers();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to extend access");
      setTimeout(() => setError(""), 3000);
    }
  };

  const resetPassword = async (userId) => {
    if (!window.confirm("Reset password for this user?")) return;
    try {
      const res = await axios.post(`${API}/admin/users/${userId}/reset-password`, {}, { headers });
      alert(`New Password: ${res.data.new_password}\n\nSave it now!`);
      setSuccess("Password reset successfully");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reset password");
      setTimeout(() => setError(""), 3000);
    }
  };

  const updateContactStatus = async (requestId, status) => {
    try {
      await axios.put(`${API}/admin/contact-requests/${requestId}?status=${status}`, {}, { headers });
      setSuccess(`Request ${status}`);
      fetchContactRequests();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update request");
      setTimeout(() => setError(""), 3000);
    }
  };

  const deleteContactRequest = async (requestId) => {
    if (!window.confirm("Delete this request?")) return;
    try {
      await axios.delete(`${API}/admin/contact-requests/${requestId}`, { headers });
      setSuccess("Request deleted");
      fetchContactRequests();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete request");
      setTimeout(() => setError(""), 3000);
    }
  };

  const createUserFromRequest = (req) => {
    setNewUser({
      name: req.name,
      email: req.email,
      phone: req.phone,
      password: Math.random().toString(36).slice(-8),
      role: "user",
      expires_in_hours: "168", // 1 week default
    });
    setShowAddModal(true);
  };

  const pendingRequests = contactRequests.filter(r => r.status === "pending").length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-cyan-50 p-3 sm:p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4 sm:mb-6">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Button variant="ghost" size="icon" onClick={onBack} data-testid="back-btn" className="shrink-0 h-9 w-9">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-pink-500 shrink-0" />
            <h1 className="text-lg sm:text-2xl font-bold text-pink-600 truncate">Admin Panel</h1>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowProfileModal(true)}
              className="border-purple-400 text-purple-600 hover:bg-purple-50 h-9 text-xs sm:text-sm"
            >
              <Settings className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">My Profile</span>
            </Button>
            <Button variant="outline" size="icon" onClick={() => { fetchUsers(); fetchContactRequests(); }} disabled={loading} className="h-9 w-9 border-pink-300">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button onClick={() => setShowAddModal(true)} data-testid="add-user-btn" size="sm" className="bg-pink-500 hover:bg-pink-600 h-9 text-xs sm:text-sm">
              <Plus className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Add User</span>
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 sm:mb-6 overflow-x-auto pb-1">
          <Button
            variant={activeTab === "users" ? "default" : "outline"}
            onClick={() => setActiveTab("users")}
            size="sm"
            className={`h-9 text-xs sm:text-sm shrink-0 ${activeTab === "users" ? "bg-pink-500 hover:bg-pink-600" : "border-pink-300 text-pink-700"}`}
          >
            <Users className="w-4 h-4 mr-1.5" /> Users ({users.length})
          </Button>
          <Button
            variant={activeTab === "requests" ? "default" : "outline"}
            onClick={() => setActiveTab("requests")}
            size="sm"
            className={`h-9 text-xs sm:text-sm shrink-0 ${activeTab === "requests" ? "bg-purple-500 hover:bg-purple-600" : "border-purple-300 text-purple-700"}`}
          >
            <MessageSquare className="w-4 h-4 mr-1.5" /> 
            Requests
            {pendingRequests > 0 && (
              <span className="ml-1.5 bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5">
                {pendingRequests}
              </span>
            )}
          </Button>
        </div>

        {/* Alerts */}
        {success && (
          <div className="bg-green-500/20 border border-green-500 text-green-400 p-3 rounded-lg mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            {success}
          </div>
        )}
        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-400 p-3 rounded-lg mb-4 flex items-center gap-2">
            <XCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {activeTab === "users" && (
          <>
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold text-white">{users.length}</p>
                  <p className="text-sm text-zinc-400">Total Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-2xl font-bold text-white">
                    {users.filter((u) => u.is_active).length}
                  </p>
                  <p className="text-sm text-zinc-400">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <XCircle className="w-8 h-8 text-red-500" />
                <div>
                  <p className="text-2xl font-bold text-white">
                    {users.filter((u) => !u.is_active).length}
                  </p>
                  <p className="text-sm text-zinc-400">Inactive</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 text-yellow-500" />
                <div>
                  <p className="text-2xl font-bold text-white">
                    {users.filter((u) => u.expires_at && u.is_active).length}
                  </p>
                  <p className="text-sm text-zinc-400">Time Limited</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Users Table */}
        <Card className="bg-white border-2 border-pink-200 rounded-2xl">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-pink-600 text-base sm:text-lg">Users</CardTitle>
            <CardDescription className="text-zinc-500 text-xs sm:text-sm">
              Manage users and their permissions
            </CardDescription>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <ScrollArea className="h-[500px] sm:h-[600px]">
              <div className="space-y-3">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="bg-white p-3 sm:p-4 rounded-2xl border-2 border-pink-200 shadow-sm"
                    data-testid={`user-card-${user.id}`}
                  >
                    <div className="flex flex-col gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <div
                          className={`shrink-0 p-2 rounded-full ${
                            user.role === "admin" ? "bg-purple-100" : "bg-pink-100"
                          }`}
                        >
                          {user.role === "admin" ? (
                            <Shield className="w-5 h-5 text-purple-600" />
                          ) : (
                            <User className="w-5 h-5 text-pink-600" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-zinc-800 truncate">{user.name}</p>
                            <Badge
                              className={`text-[10px] ${
                                user.role === "admin" ? "bg-purple-500" : "bg-pink-500"
                              }`}
                            >
                              {user.role === "admin" ? "Admin" : "User"}
                            </Badge>
                            <Badge
                              className={`text-[10px] ${user.membership_active ? "bg-emerald-500" : "bg-red-500"}`}
                            >
                              {user.role === "admin"
                                ? "Active"
                                : user.membership_active
                                ? (user.time_remaining || "Active")
                                : "Expired"}
                            </Badge>
                          </div>
                          <p className="text-xs sm:text-sm text-zinc-600 truncate">{user.email}</p>
                          {user.phone && (
                            <p className="text-xs text-zinc-500 flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {user.phone}
                            </p>
                          )}
                          {/* Tier Privilege Badges (independent toggles) */}
                          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                            {user.tier_advanced && (
                              <Badge variant="outline" className="text-[10px] border-purple-400 text-purple-700 bg-purple-50">
                                <TrendingUp className="w-3 h-3 mr-1" /> Advance
                              </Badge>
                            )}
                            {user.tier_basic && (
                              <Badge variant="outline" className="text-[10px] border-pink-400 text-pink-700 bg-pink-50">
                                <TrendingUp className="w-3 h-3 mr-1" /> Basic
                              </Badge>
                            )}
                            {user.tier_education && (
                              <Badge variant="outline" className="text-[10px] border-zinc-400 text-zinc-700 bg-zinc-50">
                                <GraduationCap className="w-3 h-3 mr-1" /> Education
                              </Badge>
                            )}
                            {!user.tier_advanced && !user.tier_basic && !user.tier_education && user.role !== "admin" && (
                              <Badge variant="outline" className="text-[10px] border-red-300 text-red-600 bg-red-50">
                                No tier
                              </Badge>
                            )}
                            {user.can_access_training && (
                              <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-600 bg-blue-50">
                                <GraduationCap className="w-3 h-3 mr-1" /> Training
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1.5 text-[10px] text-zinc-400 flex-wrap">
                            <span>Created: {new Date(user.created_at).toLocaleDateString()}</span>
                            {!user.expires_at && (
                              <span className="text-emerald-500">Unlimited expires_at</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-1.5 sm:gap-2">
                        {user.role !== "admin" && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => toggleUserStatus(user.id, user.is_active)}
                              className={`h-9 text-xs ${user.is_active ? "border-emerald-400 text-emerald-600 hover:bg-emerald-50" : "border-zinc-400 text-zinc-600 hover:bg-zinc-50"}`}
                              data-testid={`toggle-active-${user.id}`}
                            >
                              {user.is_active ? <Power className="w-3 h-3 mr-1" /> : <PowerOff className="w-3 h-3 mr-1" />}
                              {user.is_active ? "On" : "Off"}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => renewMembership(user.id, user.name)}
                              className="border-emerald-400 text-emerald-600 hover:bg-emerald-50 h-9 text-xs"
                              data-testid={`renew-user-${user.id}`}
                            >
                              <RefreshCw className="w-3 h-3 mr-1" /> Activate
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => resetDevice(user.id)}
                              className="border-cyan-400 text-cyan-600 hover:bg-cyan-50 h-9 text-xs"
                              data-testid={`reset-device-${user.id}`}
                            >
                              <Power className="w-3 h-3 mr-1" /> Device
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setHistoryUser({ id: user.id, name: user.name })}
                              className="border-purple-400 text-purple-600 hover:bg-purple-50 h-9 text-xs"
                              data-testid={`history-${user.id}`}
                            >
                              <History className="w-3 h-3 mr-1" /> History
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => resetPassword(user.id)}
                              className="border-yellow-400 text-yellow-600 hover:bg-yellow-50 h-9 text-xs"
                            >
                              <Key className="w-3 h-3 mr-1" /> Pass
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditUser(user)}
                              className="border-blue-400 text-blue-600 hover:bg-blue-50 h-9 text-xs"
                            >
                              <Edit className="w-3 h-3 mr-1" /> Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => deleteUser(user.id)}
                              className="border-red-400 text-red-600 hover:bg-red-50 h-9 text-xs"
                            >
                              <Trash2 className="w-3 h-3 mr-1" /> Delete
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {users.length === 0 && (
                  <p className="text-center text-zinc-500 py-8">No users found</p>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
          </>
        )}

        {/* Contact Requests Tab */}
        {activeTab === "requests" && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-purple-400" />
                Contact Requests
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Review and manage account requests
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-3">
                  {contactRequests.map((req) => (
                    <div
                      key={req.id}
                      className={`bg-zinc-800 p-4 rounded-lg border ${
                        req.status === "pending" ? "border-yellow-500/50" : "border-zinc-700"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <User className="w-4 h-4 text-blue-400" />
                            <span className="font-semibold text-white">{req.name}</span>
                            <Badge className={
                              req.status === "pending" ? "bg-yellow-600" :
                              req.status === "approved" ? "bg-green-600" :
                              req.status === "rejected" ? "bg-red-600" : "bg-zinc-600"
                            }>
                              {req.status}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-zinc-400">
                            <div className="flex items-center gap-2">
                              <Mail className="w-3 h-3" /> {req.email}
                            </div>
                            <div className="flex items-center gap-2">
                              <Phone className="w-3 h-3" /> {req.phone}
                            </div>
                            <div className="flex items-center gap-2">
                              <Globe className="w-3 h-3" /> {req.country}
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-3 h-3" /> {new Date(req.created_at).toLocaleString()}
                            </div>
                          </div>
                          <div className="mt-3 p-2 bg-zinc-900 rounded text-sm text-zinc-300">
                            <strong>Request:</strong> {req.request}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 ml-4">
                          {req.status === "pending" && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => createUserFromRequest(req)}
                                className="bg-green-600 hover:bg-green-700 text-xs"
                              >
                                <UserPlus className="w-3 h-3 mr-1" /> Create User
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => updateContactStatus(req.id, "approved")}
                                className="border-green-500 text-green-400 hover:bg-green-500/20 text-xs"
                              >
                                <CheckCircle className="w-3 h-3 mr-1" /> Approve
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => updateContactStatus(req.id, "rejected")}
                                className="border-red-500 text-red-400 hover:bg-red-500/20 text-xs"
                              >
                                <Ban className="w-3 h-3 mr-1" /> Reject
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteContactRequest(req.id)}
                            className="text-red-400 hover:bg-red-500/20 text-xs"
                          >
                            <Trash2 className="w-3 h-3 mr-1" /> Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {contactRequests.length === 0 && (
                    <p className="text-center text-zinc-500 py-8">No contact requests</p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Add User Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 overflow-y-auto py-4 px-2">
            <Card className="w-full max-w-md bg-zinc-900 border-zinc-700 my-2">
              <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-zinc-900 z-10 border-b border-zinc-800">
                <CardTitle className="text-white">Add New User</CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setShowAddModal(false)} data-testid="close-add-modal">
                  <X className="w-5 h-5" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">Name</label>
                  <Input
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                    placeholder="User name"
                    data-testid="new-user-name"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">Email</label>
                  <Input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                    placeholder="email@example.com"
                    data-testid="new-user-email"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">Phone</label>
                  <Input
                    type="tel"
                    value={newUser.phone}
                    onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                    placeholder="+1234567890"
                    data-testid="new-user-phone"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">Password</label>
                  <Input
                    type="text"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                    placeholder="Password"
                    data-testid="new-user-password"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">Role</label>
                  <Select
                    value={newUser.role}
                    onValueChange={(v) => setNewUser({ ...newUser, role: v })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">
                    Access Duration (hours) - Leave empty for unlimited access
                  </label>
                  <Input
                    type="number"
                    value={newUser.expires_in_hours}
                    onChange={(e) => setNewUser({ ...newUser, expires_in_hours: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                    placeholder="e.g., 24 for 1 day"
                    data-testid="new-user-expires"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    24 = 1 day, 168 = 1 week, 720 = 1 month
                  </p>
                </div>
                
                {/* User Privileges Section */}
                <div className="border-t border-zinc-700 pt-4">
                  <label className="text-sm text-zinc-300 font-semibold block mb-3">Subscription Privileges (toggle each independently)</label>
                  <div className="space-y-2" data-testid="new-user-tier-group">
                    <ToggleRow
                      label="Education (Free)"
                      desc="Educational content / strategies pages only"
                      checked={newUser.tier_education}
                      onChange={(v) => setNewUser({ ...newUser, tier_education: v })}
                      testid="new-user-tier-education"
                      color="zinc"
                    />
                    <ToggleRow
                      label="Basic ($20)"
                      desc="Gold (XAU/USD) Combination signal on 15min only"
                      checked={newUser.tier_basic}
                      onChange={(v) => setNewUser({ ...newUser, tier_basic: v })}
                      testid="new-user-tier-basic"
                      color="pink"
                    />
                    <ToggleRow
                      label="Advance ($40)"
                      desc="Full access to all markets, timeframes & strategies"
                      checked={newUser.tier_advanced}
                      onChange={(v) => setNewUser({ ...newUser, tier_advanced: v })}
                      testid="new-user-tier-advanced"
                      color="purple"
                    />
                  </div>
                  <p className="text-[10px] text-zinc-500 mt-2">
                    Each toggle is independent. Highest active tier wins: Advance &gt; Basic &gt; Education.
                  </p>
                </div>

                {/* Active toggle + activate days */}
                <div className="border-t border-zinc-700 pt-4 space-y-3">
                  <label className="text-sm text-zinc-300 font-semibold block">Account State</label>
                  <div className="flex items-center gap-3 p-2 bg-zinc-800/50 rounded">
                    <Checkbox
                      id="new-active"
                      checked={newUser.is_active}
                      onCheckedChange={(checked) => setNewUser({ ...newUser, is_active: !!checked })}
                      data-testid="new-user-active"
                    />
                    <label htmlFor="new-active" className="text-sm text-zinc-300 cursor-pointer flex items-center gap-2">
                      <Power className="w-4 h-4 text-emerald-400" />
                      <span className="font-medium">Active immediately</span>
                    </label>
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 block mb-1">Auto-activate membership for N days (optional)</label>
                    <Input
                      type="number"
                      value={newUser.activate_days}
                      onChange={(e) => setNewUser({ ...newUser, activate_days: e.target.value })}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="e.g., 30"
                      data-testid="new-user-activate-days"
                    />
                  </div>
                </div>

                {/* Legacy privileges (training) */}
                <div className="border-t border-zinc-700 pt-4">
                  <label className="text-sm text-zinc-300 font-semibold block mb-3">Extra Privileges</label>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 p-2 bg-zinc-800/50 rounded">
                      <Checkbox
                        id="signals"
                        checked={newUser.can_view_signals}
                        onCheckedChange={(checked) => setNewUser({ ...newUser, can_view_signals: checked })}
                        data-testid="new-user-signals"
                      />
                      <label htmlFor="signals" className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                        <TrendingUp className="w-4 h-4 text-green-400" />
                        <div>
                          <span className="font-medium">View Trading Signals (legacy)</span>
                          <p className="text-xs text-zinc-500">Access to BUY/SELL signals and analysis</p>
                        </div>
                      </label>
                    </div>
                    <div className="flex items-center space-x-3 p-2 bg-zinc-800/50 rounded">
                      <Checkbox
                        id="training"
                        checked={newUser.can_access_training}
                        onCheckedChange={(checked) => setNewUser({ ...newUser, can_access_training: checked })}
                        data-testid="new-user-training"
                      />
                      <label htmlFor="training" className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                        <GraduationCap className="w-4 h-4 text-blue-400" />
                        <div>
                          <span className="font-medium">Training & Education</span>
                          <p className="text-xs text-zinc-500">Access to tutorials and paper trading</p>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
                
                <Button onClick={createUser} className="w-full" data-testid="create-user-btn">
                  Create User
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Edit User Modal */}
        {editUser && (
          <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 overflow-y-auto py-4 px-2">
            <Card className="w-full max-w-md bg-zinc-900 border-zinc-700 my-2">
              <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-zinc-900 z-10 border-b border-zinc-800">
                <CardTitle className="text-white">Edit User</CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setEditUser(null)} data-testid="close-edit-modal">
                  <X className="w-5 h-5" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">Name</label>
                  <Input
                    value={editUser.name}
                    onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">Email</label>
                  <Input
                    type="email"
                    value={editUser.email}
                    onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">Phone</label>
                  <Input
                    type="tel"
                    value={editUser.phone || ""}
                    onChange={(e) => setEditUser({ ...editUser, phone: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                    placeholder="+1234567890"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">Role</label>
                  <Select
                    value={editUser.role}
                    onValueChange={(v) => setEditUser({ ...editUser, role: v })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">Status</label>
                  <Select
                    value={editUser.is_active ? "active" : "inactive"}
                    onValueChange={(v) =>
                      setEditUser({ ...editUser, is_active: v === "active" })
                    }
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Tier toggles (Edit) — 3 independent switches */}
                <div className="border-t border-zinc-700 pt-4">
                  <label className="text-sm text-zinc-300 font-semibold block mb-3">Subscription Privileges (toggle each independently)</label>
                  <div className="space-y-2" data-testid="edit-user-tier-group">
                    <ToggleRow
                      label="Education (Free)"
                      desc="Educational content / strategies pages only"
                      checked={editUser.tier_education ?? (editUser.signal_tier === "none" || !editUser.signal_tier)}
                      onChange={(v) => setEditUser({ ...editUser, tier_education: v })}
                      testid="edit-user-tier-education"
                      color="zinc"
                    />
                    <ToggleRow
                      label="Basic ($20)"
                      desc="Gold (XAU/USD) Combination signal on 15min only"
                      checked={editUser.tier_basic ?? (editUser.signal_tier === "basic")}
                      onChange={(v) => setEditUser({ ...editUser, tier_basic: v })}
                      testid="edit-user-tier-basic"
                      color="pink"
                    />
                    <ToggleRow
                      label="Advance ($40)"
                      desc="Full access to all markets, timeframes & strategies"
                      checked={editUser.tier_advanced ?? (editUser.signal_tier === "advanced")}
                      onChange={(v) => setEditUser({ ...editUser, tier_advanced: v })}
                      testid="edit-user-tier-advanced"
                      color="purple"
                    />
                  </div>
                  <p className="text-[10px] text-zinc-500 mt-2">
                    Each toggle is independent. Highest active tier wins: Advance &gt; Basic &gt; Education.
                  </p>
                </div>

                {/* User Privileges Section */}
                <div className="border-t border-zinc-700 pt-4">
                  <label className="text-sm text-zinc-300 font-semibold block mb-3">Extra Privileges</label>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 p-2 bg-zinc-800/50 rounded">
                      <Checkbox
                        id="edit-signals"
                        checked={editUser.can_view_signals ?? true}
                        onCheckedChange={(checked) => setEditUser({ ...editUser, can_view_signals: checked })}
                      />
                      <label htmlFor="edit-signals" className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                        <TrendingUp className="w-4 h-4 text-green-400" />
                        <div>
                          <span className="font-medium">View Trading Signals</span>
                          <p className="text-xs text-zinc-500">Access to BUY/SELL signals</p>
                        </div>
                      </label>
                    </div>
                    <div className="flex items-center space-x-3 p-2 bg-zinc-800/50 rounded">
                      <Checkbox
                        id="edit-training"
                        checked={editUser.can_access_training ?? false}
                        onCheckedChange={(checked) => setEditUser({ ...editUser, can_access_training: checked })}
                      />
                      <label htmlFor="edit-training" className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                        <GraduationCap className="w-4 h-4 text-blue-400" />
                        <div>
                          <span className="font-medium">Training & Education</span>
                          <p className="text-xs text-zinc-500">Tutorials and paper trading</p>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
                
                <Button onClick={updateUser} className="w-full">
                  Save Changes
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Profile Modal (Admin's own profile) */}
        {showProfileModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <Card className="w-full max-w-md bg-zinc-900 border-zinc-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Settings className="w-5 h-5" /> My Profile
                </CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setShowProfileModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">Name</label>
                  <Input
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 block mb-1">Phone</label>
                  <Input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                    placeholder="+1234567890"
                  />
                </div>
                <div className="border-t border-zinc-700 pt-4 mt-4">
                  <p className="text-sm text-zinc-300 mb-3">Change Password (optional)</p>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-zinc-400 block mb-1">Current Password</label>
                      <Input
                        type="password"
                        value={profileData.current_password}
                        onChange={(e) => setProfileData({ ...profileData, current_password: e.target.value })}
                        className="bg-zinc-800 border-zinc-700"
                        placeholder="Enter current password"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-zinc-400 block mb-1">New Password</label>
                      <Input
                        type="password"
                        value={profileData.new_password}
                        onChange={(e) => setProfileData({ ...profileData, new_password: e.target.value })}
                        className="bg-zinc-800 border-zinc-700"
                        placeholder="Enter new password"
                      />
                    </div>
                  </div>
                </div>
                <Button onClick={updateProfile} className="w-full bg-purple-600 hover:bg-purple-700">
                  Save Profile
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Login History Modal */}
      <LoginHistoryModal
        open={!!historyUser}
        onClose={() => setHistoryUser(null)}
        userId={historyUser?.id}
        userName={historyUser?.name}
        token={token}
      />
    </div>
  );
}

// Reusable toggle row for the 3 independent tier privileges
function ToggleRow({ label, desc, checked, onChange, testid, color = "zinc" }) {
  const onBg = {
    zinc: "bg-zinc-500/20 border-zinc-400",
    pink: "bg-pink-500/20 border-pink-500",
    purple: "bg-purple-500/20 border-purple-500",
  }[color];
  const dot = {
    zinc: "bg-zinc-300",
    pink: "bg-pink-400",
    purple: "bg-purple-400",
  }[color];
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className={`w-full flex items-center justify-between gap-3 p-2.5 rounded-lg border-2 transition-all text-left ${
        checked ? onBg : "bg-zinc-800 border-zinc-700"
      }`}
    >
      <div className="min-w-0">
        <p className={`text-xs font-bold ${checked ? "text-white" : "text-zinc-300"}`}>{label}</p>
        <p className="text-[10px] mt-0.5 text-zinc-400 truncate">{desc}</p>
      </div>
      <div
        className={`relative shrink-0 w-10 h-5 rounded-full transition-colors ${
          checked ? "bg-emerald-500" : "bg-zinc-600"
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full ${dot} shadow transition-transform ${
            checked ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </div>
    </button>
  );
}

